import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function ElegantTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, Georgia, 'Times New Roman', serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="pt-8 pb-6" style={{ textAlign: s.headerAlignment }}>
        {s.photo && <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border border-slate-300" />}
        <h1 className="font-light text-slate-900 tracking-wide" style={{ fontFamily: `${s.fonts.secondary}, Georgia, serif`, fontSize: `${3 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-500 tracking-wide justify-center">
          {personal.email && <span>{personal.email}</span>}
          {personal.phone && <span className="text-slate-300">|</span>}
          {personal.phone && <span>{personal.phone}</span>}
          {personal.location && <span className="text-slate-300">|</span>}
          {personal.location && <span>{personal.location}</span>}
          {personal.linkedin && <span className="text-slate-300">|</span>}
          {personal.linkedin && <span>{personal.linkedin}</span>}
          {personal.website && <span className="text-slate-300">|</span>}
          {personal.website && <span>{personal.website}</span>}
        </div>
        <div className="mt-6 mx-auto h-px bg-slate-300" style={{ width: '4rem' }}></div>
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.25em] text-slate-400 mb-4" style={{ fontSize: `${0.75 * hScale}rem` }}>WORK EXPERIENCE</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <div key={exp.id} className="grid grid-cols-[1fr_auto] gap-4">
                  <div>
                    <h3 className="font-normal text-slate-900" style={{ fontSize: `${1.125 * hScale}rem` }}>{exp.title}</h3>
                    <p className="text-slate-600 mt-1 italic" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.company}{exp.location && `, ${exp.location}`}</p>
                    {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                  </div>
                  <span className="text-slate-400 font-light whitespace-nowrap" style={{ fontSize: `${0.875 * bScale}rem` }}>
                    {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.25em] text-slate-400 mb-4" style={{ fontSize: `${0.75 * hScale}rem` }}>Education</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <div key={edu.id} className="flex justify-between items-baseline pb-3 border-b border-slate-100">
                  <div>
                    <h3 className="font-normal text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{edu.school}</h3>
                    <p className="text-slate-600 italic" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                  </div>
                  <span className="text-slate-400" style={{ fontSize: `${0.875 * bScale}rem` }}>
                    {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.25em] text-slate-400 mb-4" style={{ fontSize: `${0.75 * hScale}rem` }}>Skills</h2>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {skills.map((skill) => (
                <span key={skill.id} className="inline-flex items-center rounded-md bg-slate-700 px-3 py-1.5 text-xs font-medium text-white" style={{ fontSize: `${0.75 * bScale}rem` }}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.25em] text-slate-400 mb-4" style={{ fontSize: `${0.75 * hScale}rem` }}>Projects</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((proj) => (
                <div key={proj.id} className="pb-3 border-b border-slate-100">
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-normal text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{proj.name}</h3>
                    {proj.url && <a href={proj.url} className="text-slate-400 hover:text-slate-600 underline" style={{ fontSize: `${0.75 * bScale}rem` }}>View</a>}
                  </div>
                  <p className="text-slate-600 mt-1" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && <p className="text-slate-400 mt-2 italic" style={{ fontSize: `${0.75 * bScale}rem` }}>{proj.technologies.join(', ')}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-[0.25em] text-slate-400 mb-4" style={{ fontSize: `${0.75 * hScale}rem` }}>Certifications</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {certifications.map((cert) => (
                <div key={cert.id} className="flex justify-between items-baseline">
                  <span className="text-slate-700" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.name}</span>
                  <span className="text-slate-400" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer} · {cert.date}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
